import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SimulationEngine } from './engine/SimulationEngine';
import apapScenario from './scenarios/apap';
import PathwayDiagram from './components/PathwayDiagram';
import ResourceBars from './components/ResourceBars';
import Controls from './components/Controls';
import FeedbackPanel from './components/FeedbackPanel';
import ExperimentLog from './components/ExperimentLog';
import './App.css';

function App() {
  const engineRef = useRef(null);
  const intervalRef = useRef(null);

  const [simState, setSimState] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [summary, setSummary] = useState(null);
  const [totalDose, setTotalDose] = useState(0);

  // Initialize engine
  useEffect(() => {
    const engine = new SimulationEngine(apapScenario);
    engineRef.current = engine;
    setSimState(engine.getState());
  }, []);

  // Simulation loop
  useEffect(() => {
    if (isRunning && engineRef.current && !engineRef.current.isComplete) {
      const baseInterval = 500; // ms per tick at 1x
      const interval = baseInterval / speed;

      intervalRef.current = setInterval(() => {
        const engine = engineRef.current;
        if (engine.isComplete) {
          setIsRunning(false);
          setSummary(engine.generateSummary(totalDose));
          clearInterval(intervalRef.current);
          return;
        }
        engine.tick();
        setSimState({ ...engine.getState() });
      }, interval);

      return () => clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, speed]);

  const handleTogglePlay = useCallback(() => {
    setIsRunning(prev => !prev);
  }, []);

  const handleSetSpeed = useCallback((newSpeed) => {
    setSpeed(newSpeed);
  }, []);

  const handleAdministerDose = useCallback((amount) => {
    if (!engineRef.current) return;
    engineRef.current.addDose('APAP', amount);
    engineRef.current.addFeedback(
      `Dose administered: ${amount} units of APAP entering the system.`,
      'info'
    );
    const preset = apapScenario.dosePresets.find(p => p.amount === amount);
    setLastDoseLabel(preset ? preset.label : `Custom (${amount})`);
    setTotalDose(prev => prev + amount);
    setSimState({ ...engineRef.current.getState() });
  }, []);

  const handleToggleModifier = useCallback((modId, value) => {
    if (!engineRef.current) return;
    // Mutual exclusion: Fast and Slow can't both be active
    if (modId === 'cyp2e1Fast' && value) {
      engineRef.current.setModifier('cyp2e1Slow', false);
    }
    if (modId === 'cyp2e1Slow' && value) {
      engineRef.current.setModifier('cyp2e1Fast', false);
    }
    engineRef.current.setModifier(modId, value);
    setSimState({ ...engineRef.current.getState() });
  }, []);

  const handleActivateIntervention = useCallback((interventionId) => {
    if (!engineRef.current) return;
    engineRef.current.activateIntervention(interventionId);
    setSimState({ ...engineRef.current.getState() });
  }, []);

  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [experiments, setExperiments] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('apap_experiments') || '[]');
    } catch { return []; }
  });
  const [lastDoseLabel, setLastDoseLabel] = useState('');
  const experimentIdRef = useRef(
    experiments.length > 0 ? Math.max(...experiments.map(e => e.id)) : 0
  );

  // Persist experiments to localStorage
  useEffect(() => {
    localStorage.setItem('apap_experiments', JSON.stringify(experiments));
  }, [experiments]);

  // Show popup when summary is generated
  useEffect(() => {
    if (summary) setShowSummaryPopup(true);
  }, [summary]);

  const handleReset = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.reset();
    setSimState({ ...engineRef.current.getState() });
    setIsRunning(false);
    setSummary(null);
    setShowSummaryPopup(false);
    setTotalDose(0);
  }, []);

  const handleToggleMode = useCallback(() => {
    setAdvancedMode(prev => !prev);
  }, []);

  const handleSaveExperiment = useCallback(() => {
    if (!summary) return;
    const activeModifiers = [];
    if (simState?.modifiers) {
      for (const mod of apapScenario.modifiers) {
        if (simState.modifiers[mod.id]) {
          activeModifiers.push(mod.name || mod.id);
        }
      }
    }
    experimentIdRef.current += 1;
    const experiment = {
      id: experimentIdRef.current,
      timestamp: new Date().toLocaleString(),
      doseLabel: lastDoseLabel,
      doseAmount: totalDose,
      modifiers: activeModifiers,
      nacUsed: summary.nacUsed,
      nacTiming: summary.nacTiming,
      outcome: summary.outcome,
      ticksSurvived: summary.ticksSurvived,
      finalHealth: summary.finalHealth,
      finalMitoIntegrity: summary.finalMitoIntegrity,
      gshRemaining: summary.gshRemaining,
      glucuronidePct: summary.glucuronidePct,
      sulfatePct: summary.sulfatePct,
      gshConjugatePct: summary.gshConjugatePct,
      napqiAmount: summary.napqiAmount,
      apapRemaining: summary.apapRemaining,
      factors: summary.factors,
    };
    setExperiments(prev => [...prev, experiment]);
  }, [summary, simState, lastDoseLabel, totalDose]);

  const handleDeleteExperiment = useCallback((id) => {
    setExperiments(prev => prev.filter(e => e.id !== id));
  }, []);

  const handleClearExperiments = useCallback(() => {
    setExperiments([]);
    experimentIdRef.current = 0;
  }, []);

  // Draggable popup logic
  const popupRef = useRef(null);
  const dragState = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const [popupPos, setPopupPos] = useState({ x: 20, y: 60 });

  const onDragMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    setPopupPos({
      x: e.clientX - dragState.current.offsetX,
      y: e.clientY - dragState.current.offsetY,
    });
  }, []);

  const onDragEnd = useCallback(() => {
    dragState.current.dragging = false;
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
  }, [onDragMove]);

  const onDragStart = useCallback((e) => {
    if (e.target.tagName === 'BUTTON') return;
    dragState.current = {
      dragging: true,
      offsetX: e.clientX - popupPos.x,
      offsetY: e.clientY - popupPos.y,
    };
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
  }, [popupPos, onDragMove, onDragEnd]);

  return (
    <div className="app">
      <header className="app-header">
        <h1>Biochemical Systems: {apapScenario.name}</h1>
        <p className="subtitle">
          Manage metabolic pathways to keep the hepatocyte alive
        </p>
      </header>

      <main className="app-main">
        <div className="left-panel">
          <Controls
            isRunning={isRunning}
            speed={speed}
            onTogglePlay={handleTogglePlay}
            onSetSpeed={handleSetSpeed}
            onAdministerDose={handleAdministerDose}
            onToggleModifier={handleToggleModifier}
            onActivateIntervention={handleActivateIntervention}
            onReset={handleReset}
            onToggleMode={handleToggleMode}
            advancedMode={advancedMode}
            scenario={apapScenario}
            simState={simState}
            isComplete={engineRef.current?.isComplete || false}
          />
          <ExperimentLog
            experiments={experiments}
            onSave={handleSaveExperiment}
            onDelete={handleDeleteExperiment}
            onClear={handleClearExperiments}
            currentCanSave={!!summary}
          />
        </div>

        <div className="center-panel">
          <PathwayDiagram
            scenario={apapScenario}
            simState={simState}
            advancedMode={advancedMode}
            isRunning={isRunning}
            totalDose={totalDose}
          />
        </div>

        <div className="right-panel">
          <ResourceBars
            simState={simState}
            scenario={apapScenario}
            advancedMode={advancedMode}
          />
          <FeedbackPanel
            feedbackLog={simState?.feedbackLog || []}
            isComplete={simState?.isComplete || false}
            outcome={simState?.outcome}
            summary={summary}
          />
        </div>
      </main>

      {showSummaryPopup && summary && (
          <div className="summary-popup draggable" ref={popupRef}
            style={{ left: popupPos.x, top: popupPos.y }}
            onMouseDown={onDragStart}>
            <div className="popup-drag-handle">Drag to move</div>
            <button className="summary-close" onClick={() => setShowSummaryPopup(false)}>&times;</button>
            <h2 className={summary.outcome === 'win' ? 'popup-win' : 'popup-lose'}>
              {summary.outcome === 'win' ? 'Hepatocyte Survived!' : 'Hepatocyte Lost'}
            </h2>

            <div className="popup-section">
              <h3>What Happened to the Drug</h3>
              <div className="popup-fate-grid">
                <div className="fate-item">
                  <span className="fate-pct">{summary.apapRemaining.toFixed(0)}%</span>
                  <span className="fate-label">APAP remaining</span>
                </div>
                <div className="fate-item safe">
                  <span className="fate-pct">{summary.glucuronidePct.toFixed(0)}%</span>
                  <span className="fate-label">Glucuronidation (UGT)</span>
                </div>
                <div className="fate-item safe">
                  <span className="fate-pct">{summary.sulfatePct.toFixed(0)}%</span>
                  <span className="fate-label">Sulfation (SULT)</span>
                </div>
                <div className="fate-item safe">
                  <span className="fate-pct">{summary.gshConjugatePct.toFixed(0)}%</span>
                  <span className="fate-label">GSH conjugation</span>
                </div>
                <div className="fate-item toxic">
                  <span className="fate-pct">{summary.napqiAmount.toFixed(1)}</span>
                  <span className="fate-label">NAPQI (unconjugated)</span>
                </div>
              </div>
            </div>

            <div className="popup-section">
              <h3>Why This Happened</h3>
              <div className="popup-explanation">
                {summary.outcome === 'win' && (
                  <>
                    <p>
                      The hepatocyte successfully processed the acetaminophen dose.
                      The safe pathways handled the majority of clearance:
                      glucuronidation cleared {summary.glucuronidePct.toFixed(0)}% of the dose,
                      and sulfation cleared {summary.sulfatePct.toFixed(0)}%.
                    </p>
                    <p>
                      A small fraction (~{summary.gshConjugatePct.toFixed(0)}%) was routed through
                      the CYP enzymes and converted to the toxic intermediate NAPQI. Glutathione
                      conjugation neutralized this NAPQI, producing the detox product you see
                      on the pathway diagram.
                    </p>
                    <p>
                      You may have noticed GSH dipping slightly during metabolism — each NAPQI
                      molecule neutralized costs glutathione. At this dose, GSH regeneration
                      ({summary.gshRemaining.toFixed(0)}% remaining at end) easily kept pace with
                      consumption, and the pool recovered. Try a higher dose to see what happens
                      when NAPQI production overwhelms GSH regeneration.
                    </p>
                  </>
                )}
                {summary.outcome === 'lose' && summary.gshDepleted && (
                  <>
                    <p>
                      The safe pathways attempted to clear the dose: glucuronidation
                      processed {summary.glucuronidePct.toFixed(0)}% and
                      sulfation processed {summary.sulfatePct.toFixed(0)}%.
                      However, these pathways could not keep up with the large dose,
                      leaving significant APAP for the CYP enzymes.
                    </p>
                    <p>
                      The CYP enzymes converted the excess APAP into NAPQI — the toxic
                      intermediate. As NAPQI accumulated, glutathione was consumed trying
                      to neutralize it. Once GSH fell
                      below 30% of normal — the Mitchell threshold — conjugation failed
                      and NAPQI began binding directly to hepatocyte proteins.
                    </p>
                    <p>
                      <strong>GSH was depleted</strong> ({summary.gshRemaining.toFixed(0)}% remaining).
                      With {summary.apapRemaining.toFixed(0)}% of APAP still unmetabolized,
                      CYP enzymes continued producing NAPQI with no GSH left to neutralize it.
                      The resulting oxidative stress triggered mitochondrial collapse and
                      irreversible cell damage.
                    </p>
                  </>
                )}
                {summary.outcome === 'lose' && !summary.gshDepleted && (
                  <>
                    <p>
                      The safe pathways cleared what they could: glucuronidation
                      processed {summary.glucuronidePct.toFixed(0)}% and
                      sulfation processed {summary.sulfatePct.toFixed(0)}% of the dose.
                    </p>
                    <p>
                      NAPQI production from CYP enzymes exceeded the cell's capacity
                      to maintain cellular integrity, even though glutathione was not
                      fully depleted ({summary.gshRemaining.toFixed(0)}% remaining).
                    </p>
                    <p>
                      The sustained oxidative stress from NAPQI overwhelmed mitochondrial
                      defenses, leading to irreversible hepatocyte damage.
                    </p>
                  </>
                )}
              </div>
            </div>

            {summary.factors.length > 0 && (
              <div className="popup-section">
                <h3>Key Factors</h3>
                <ul className="popup-factors">
                  {summary.factors.map((factor, i) => (
                    <li key={i}>{factor}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="popup-section">
              <h3>Clinical Connection</h3>
              <div className="popup-explanation">
                {summary.outcome === 'lose' && summary.apapRemaining > 20 && (
                  <p>
                    Notice that <strong>{summary.apapRemaining.toFixed(0)}% of the dose was still unmetabolized</strong> when
                    the cell died. This mirrors real acetaminophen toxicity — patients still have circulating
                    APAP in their blood when liver damage occurs. This is why N-acetylcysteine (NAC) can work
                    even hours after ingestion: there is still time to replenish GSH and safely clear the
                    remaining drug before more NAPQI is produced.
                  </p>
                )}
                {summary.outcome === 'lose' && summary.apapRemaining <= 20 && (
                  <p>
                    Nearly all the APAP was metabolized, but the CYP pathway produced more NAPQI than
                    the cell's GSH supply could handle. In clinical practice, this pattern is seen in
                    patients with depleted glutathione (fasting, malnutrition, chronic alcohol use).
                  </p>
                )}
                {summary.outcome === 'win' && !summary.nacUsed && (
                  <p>
                    At therapeutic doses, over 90% of APAP is cleared through safe glucuronidation
                    and sulfation pathways. The small amount of NAPQI produced is easily handled by
                    glutathione — exactly what you observed in this simulation.
                  </p>
                )}
                {summary.outcome === 'win' && summary.nacUsed && (
                  <p>
                    NAC (N-acetylcysteine) provided the cysteine precursor needed to regenerate
                    glutathione, keeping the GSH pool above the critical threshold. In clinical
                    practice, NAC is most effective within 8 hours of overdose but can still help
                    up to 24 hours after ingestion.
                  </p>
                )}
              </div>
            </div>

            <div className="popup-stats">
              <span>Cycles survived: <strong>{summary.ticksSurvived}</strong></span>
              <span>Final health: <strong>{summary.finalHealth.toFixed(1)}%</strong></span>
              <span>Mito integrity: <strong>{summary.finalMitoIntegrity.toFixed(1)}%</strong></span>
            </div>

            <div className="popup-button-row">
              <button className="popup-save" onClick={handleSaveExperiment}>Save to Log</button>
              <button className="popup-restart" onClick={handleReset}>Reset &amp; Try Again</button>
            </div>
          </div>
      )}
    </div>
  );
}

export default App;
